/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Деструктуризация</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Деструктуризация объектов</h3>
      <p className={styles.description}>
        При разработке программ данные приходят как правило в виде массивов и объектов. А логика обработки данных
        написана с помощью локальных переменных. Поэтому необходимо присваивать значения свойств объектов переменным и
        делать это желательно максимально просто.{' '}
      </p>
      <p className={styles.description}>
        Для этого в ES6 введен новый синтаксис — переменным внутри фигурных скобок присваиваются значения объекта. Если
        имя переменной и ключ объекта совпадают, то происходит присваивание. Если для переменной не нашлось подходящего
        ключа то будет присвоен <code className={styles.code}>undefined</code>.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

// Объявим переменные и присвоим им значения из объекта
const { name, stars, status } = hotel;

console.log(name, stars, status); // "Resort Hotel", 5, undefined`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Присвоения <code className={styles.code}>undefined</code> можно избежать, задав переменным значения по
        умолчанию, в случае если такого свойства в объекте нет.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

const { name = 'hotel', stars = 3, status = 'empty' } = hotel;

console.log(name, stars, status); // "Resort Hotel", 5, "empty"`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Не всегда удобно создавать имена переменных по ключам, поэтому можно переопределить имена в процессе присвоения.
        Сначала пишем имя свойства из которого мы хотим получить значение, после чего ставим двоеточие и пишем имя
        переменной в которую хотим поместить значение этого свойства.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

const { name: hotelName, stars, status: hotelStatus = 'empty' } = hotel;

console.log(hotelName, stars, hotelStatus);
// "Resort Hotel", 5, "empty"`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используя операцию <code className={styles.code}>...</code> (rest) можно делать частичную деструктуризацию, взяв
        из объекта необходимые поля, а остальное собрать в переменную под именем{' '}
        <code className={styles.code}>rest</code> (имя переменной произвольное), это будет объект с теми полями, которые
        мы явно не деструктуризировали в переменные.{' '}
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

const { name, ...rest } = hotel;

console.log(name); // "Resort Hotel"
console.log(rest); // {stars: 5, capacity: 100}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Деструктуризация массивов</h3>
      <p className={styles.description}>
        Аналогично делается деструктуризация массивов, но с некоторыми особенностями:{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Вместо <code className={styles.code}>{`{}`}</code> используются <code className={styles.code}>[]</code>
        </li>
        <li className={styles.list__item}>
          Переменным указанным в <code className={styles.code}>[]</code> будут последовательно присваиваться значения
          элементов массива
        </li>
      </ul>
      <p className={styles.description}>
        Представьте что есть массив цветов, из которого нужно в отдельные переменные получить значения каждой
        составляющей цвета. После ключевого слова <code className={styles.code}>const</code> или{' '}
        <code className={styles.code}>let</code> ставим открывающую и закрывающую квадратные скобки, как при объявлении
        массива. Внутри скобок, через запятую, указываем имена переменных в которые будут помещены значения массива.{' '}
      </p>
      <p className={styles.description}>
        В результате такой записи будут созданы 3 переменные и в них будут помещены элементы в нумерованом порядке, от 0
        до конца массива.{' '}
      </p>
      <CopyBlock
        text={`const rgb = [200, 255, 100];

const [red, green, blue] = rgb;

console.log(\`Red: $ {red}, Green: $ {green}, Blue: $ {blue}\`);
// Red: 200, Green: 255, Blue: 100`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При деструктуризации массивов переменной может быть присвоено значение отдельно от ее объявления.{' '}
      </p>
      <CopyBlock
        text={`const rgb = [200, 255, 100];
let red, green, blue;

[red, green, blue] = rgb;

console.log('Red: $ {red}, Green: $ {green}, Blue: $ {blue}');
// Red: 200, Green: 255, Blue: 100`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если переменных больше чем элементов массива, им будет присвоено <code className={styles.code}>undefined</code>,
        поэтому можно указывать значения по умолчанию.{' '}
      </p>
      <CopyBlock
        text={`const rgb = [200, 100, 255];

const [red, green, blue, alfa = 0.3] = rgb;

console.log('Red: $ {red}, Green: $ {green}, Blue: $ {blue}, Alfa: $ {alfa}');
// Red: 200, Green: 100, Blue: 255, Alfa: 0.3`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Иногда из массива необходимо деструктуризировать только первые <code className={styles.code}>n</code> элементов,
        а остальные сохранить в одну переменную в виде массива. При деструкции массива можно распаковать и присвоить
        оставшуюся часть элементов массива переменной, используя операцию <code className={styles.code}>...</code>{' '}
        (rest).{' '}
      </p>
      <CopyBlock
        text={`const rgb = [200, 255, 100];

const [red, ...colors] = rgb;

console.log(\`Red: $ {red}, Colors: $ {colors}\`);
// Red: 200, Colors: [255, 100]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Элементы можно пропускать. Допустим из массива <code className={styles.code}>rgb</code> необходимо взять только
        последнее значение. Используется это крайне редко.{' '}
      </p>
      <CopyBlock
        text={`const rgb = [200, 100, 255];

const [, , blue] = rgb;

console.log(\`Blue: $ {blue}\`); // Blue: 255`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://codeguida.com/post/1480" target="_blank">
            ES6 деструктуризація. Детальна інструкція от Codeguida
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="http://exploringjs.com/es6/ch_destructuring.html" target="_blank">
            exploring.js - Destructuring
          </a>
        </li>
      </ul>
    </section>
  </section>
))
